import { SyntheticEvent, useEffect, useRef, useState } from 'react'

import axios from 'axios'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

import TabContext from '@mui/lab/TabContext'
import TabList from '@mui/lab/TabList'
import TabPanel from '@mui/lab/TabPanel'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    TextField,
    Tooltip,
    Typography
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import Icon from '../../../components/icon'
import MsiDatagrid from '../../../components/custom/MsiDatagridAuto'
import { useToast } from '../../../components/ToastProvider'
import apiUrl from '../../../configs/constant'

type InvoiceReceiptRow = {
    id: string | number
    pbsupplier_id?: string | number | null
    nomor?: string | null
    no_invoice_receipt?: string | null
    nama_supplier?: string | null
    supplier?: string | null
    [key: string]: any
}

const getDefaultDateRange = () => ({
    tglAwal: moment().subtract(3, 'months').startOf('month').format('YYYY-MM-DD'),
    tglAkhir: moment().endOf('month').format('YYYY-MM-DD')
})

const getStoredToken = (): string => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}')

        return user?.token || localStorage.getItem('accessToken') || ''
    } catch {
        return localStorage.getItem('accessToken') || ''
    }
}

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (axios.isAxiosError(error)) {
        const responseMessage = error.response?.data?.message

        if (typeof responseMessage === 'string' && responseMessage.trim()) {
            return responseMessage
        }

        return error.message || fallback
    }

    if (error instanceof Error && error.message) {
        return error.message
    }

    return fallback
}

const getFilenameFromDisposition = (contentDisposition?: string): string | null => {
    if (!contentDisposition) return null

    const utf8Filename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)

    if (utf8Filename?.[1]) {
        try {
            return decodeURIComponent(utf8Filename[1].replace(/["']/g, '').trim())
        } catch {
            return utf8Filename[1].replace(/["']/g, '').trim()
        }
    }

    const regularFilename = contentDisposition.match(/filename="?([^";]+)"?/i)

    return regularFilename?.[1]?.trim() || null
}

const sanitizeFilename = (value: string): string =>
    value.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-')

export default function Index() {
    const theme = useTheme()
    const navigate = useNavigate()
    const { showToast } = useToast()

    const defaultDateRange = getDefaultDateRange()
    const token = getStoredToken()

    const requestControllerRef = useRef<AbortController | null>(null)

    const [tabs, setTabs] = useState<string>('0')
    const [tglAwal, setTglAwal] = useState<string>(defaultDateRange.tglAwal)
    const [tglAkhir, setTglAkhir] = useState<string>(defaultDateRange.tglAkhir)
    const [listData, setListData] = useState<InvoiceReceiptRow[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [downloadingId, setDownloadingId] = useState<string | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<InvoiceReceiptRow | null>(null)
    const [deleting, setDeleting] = useState<boolean>(false)

    const getData = async (
        mode: string,
        startDate: string = tglAwal,
        endDate: string = tglAkhir
    ): Promise<void> => {
        if (!token) {
            showToast('Token tidak ditemukan. Silakan login kembali.', 'error')

            return
        }

        requestControllerRef.current?.abort()

        const controller = new AbortController()
        requestControllerRef.current = controller

        const url =
            mode === '0'
                ? apiUrl.apiUrl + '/acc/invoice-receipt/list-all'
                : apiUrl.apiUrl + '/acc/invoice-receipt/list-gr-belum-tertagih'

        setLoading(true)
        setListData([])

        try {
            const response = await axios.get(url, {
                params: {
                    tgl_awal: startDate,
                    tgl_akhir: endDate
                },
                headers: {
                    Authorization: `Bearer ${token}`
                },
                signal: controller.signal
            })

            if (!response.data?.success) {
                showToast(response.data?.message || 'Data gagal dimuat.', 'error')

                return
            }

            setListData(Array.isArray(response.data.data) ? response.data.data : [])
        } catch (error) {
            if (axios.isAxiosError(error) && error.code === 'ERR_CANCELED') return

            showToast(getErrorMessage(error, 'Terjadi kesalahan saat mengambil data.'), 'error')
        } finally {
            if (requestControllerRef.current === controller) {
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        void getData('0')

        return () => {
            requestControllerRef.current?.abort()
        }
        // Data awal cukup dimuat satu kali ketika halaman dibuka.
    }, [])

    const handleSearch = (): void => {
        if (!tglAwal || !tglAkhir) {
            showToast('Tanggal awal dan tanggal akhir wajib diisi.', 'error')

            return
        }

        if (moment(tglAwal).isAfter(moment(tglAkhir), 'day')) {
            showToast('Tanggal awal tidak boleh lebih besar dari tanggal akhir.', 'error')

            return
        }

        void getData(tabs)
    }

    const handleResetFilter = (): void => {
        const nextDefaultDateRange = getDefaultDateRange()

        setTglAwal(nextDefaultDateRange.tglAwal)
        setTglAkhir(nextDefaultDateRange.tglAkhir)

        void getData(tabs, nextDefaultDateRange.tglAwal, nextDefaultDateRange.tglAkhir)
    }

    const handleChangeTabs = (_event: SyntheticEvent, newValue: string): void => {
        setTabs(newValue)
        void getData(newValue)
    }

    const handleDownloadPdf = async (row: InvoiceReceiptRow): Promise<void> => {
        if (row?.id === null || row?.id === undefined) {
            showToast('ID Invoice Receipt tidak ditemukan.', 'error')

            return
        }

        setDownloadingId(String(row.id))

        try {
            // Endpoint PDF menggunakan baseUrl, bukan apiUrl.apiUrl.
            const pdfBaseUrl = String(apiUrl.baseUrl).replace(/\/+$/, '')
            const url = `${pdfBaseUrl}/acc/invoice-receipt/pdf/${encodeURIComponent(String(row.id))}`

            const response = await axios.get<Blob>(url, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/pdf'
                },
                responseType: 'blob'
            })

            const contentType = String(response.headers['content-type'] || 'application/pdf').toLowerCase()

            if (contentType.includes('application/json')) {
                const responseText = await response.data.text()
                let message = 'PDF gagal dibuat.'

                try {
                    const jsonResponse = JSON.parse(responseText)
                    message = jsonResponse?.message || message
                } catch {
                    if (responseText.trim()) message = responseText
                }

                throw new Error(message)
            }

            const pdfBlob = new Blob([response.data], {
                type: contentType || 'application/pdf'
            })
            const objectUrl = window.URL.createObjectURL(pdfBlob)
            const downloadLink = document.createElement('a')
            const filenameFromHeader = getFilenameFromDisposition(response.headers['content-disposition'])
            const documentNumber = String(row.nomor || row.no_invoice_receipt || row.id)

            downloadLink.href = objectUrl
            downloadLink.download =
                filenameFromHeader || `invoice-receipt-${sanitizeFilename(documentNumber)}.pdf`
            downloadLink.style.display = 'none'

            document.body.appendChild(downloadLink)
            downloadLink.click()
            document.body.removeChild(downloadLink)

            window.setTimeout(() => {
                window.URL.revokeObjectURL(objectUrl)
            }, 1000)

            showToast('PDF Invoice Receipt berhasil diunduh.', 'success')
        } catch (error) {
            let message = getErrorMessage(error, 'Gagal mengunduh PDF Invoice Receipt.')

            if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
                try {
                    const responseText = await error.response.data.text()
                    const jsonResponse = JSON.parse(responseText)
                    message = jsonResponse?.message || message
                } catch {
                    // Gunakan pesan default ketika response blob bukan JSON.
                }
            }

            showToast(message, 'error')
        } finally {
            setDownloadingId(null)
        }
    }

    const handleDelete = async (): Promise<void> => {
        if (!deleteTarget) return

        setDeleting(true)

        try {
            const url =
                apiUrl.apiUrl +
                `/acc/invoice-receipt/hapus-dokumen/${encodeURIComponent(String(deleteTarget.id))}`

            const response = await axios.delete(url, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })

            if (!response.data?.success) {
                showToast(response.data?.message || 'Dokumen gagal dihapus.', 'error')

                return
            }

            showToast(response.data?.message || 'Dokumen berhasil dihapus.', 'success')
            setDeleteTarget(null)
            await getData(tabs)
        } catch (error) {
            showToast(getErrorMessage(error, 'Terjadi kesalahan saat menghapus dokumen.'), 'error')
        } finally {
            setDeleting(false)
        }
    }

    const handleAddFromSupplier = (row: InvoiceReceiptRow): void => {
        if (row.pbsupplier_id === null || row.pbsupplier_id === undefined) {
            showToast('ID supplier tidak ditemukan pada data GR.', 'error')

            return
        }

        navigate(
            `/acc/invoice-receipt/add?pbsupplier_id=${encodeURIComponent(String(row.pbsupplier_id))}`
        )
    }

    const getRowDocumentLabel = (row: InvoiceReceiptRow | null): string => {
        if (!row) return '-'

        return String(row.nomor || row.no_invoice_receipt || `ID ${row.id}`)
    }

    const ActionButtons = ({ row }: { row: InvoiceReceiptRow }) => {
        const isDownloading = downloadingId === String(row.id)

        return (
            <Stack direction='row' spacing={0.75} alignItems='center' justifyContent='center'>
                <Tooltip title='Lihat detail' placement='top' arrow>
                    <IconButton
                        size='small'
                        color='primary'
                        onClick={() => navigate(`/acc/invoice-receipt/detail/${row.id}`)}
                        sx={{
                            width: 34,
                            height: 34,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.primary.main, 0.24),
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.14),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        <Icon icon='mdi:eye-outline' fontSize={19} />
                    </IconButton>
                </Tooltip>

                <Tooltip title='Download PDF' placement='top' arrow>
                    <span>
                        <IconButton
                            size='small'
                            color='success'
                            // disabled={isDownloading}
                            onClick={() => {
                                window.open(
                                    `${apiUrl.baseUrl}/acc/invoice-receipt/pdf/${row.id}`,
                                    '_blank'
                                )
                            }}
                            sx={{
                                width: 34,
                                height: 34,
                                border: '1px solid',
                                borderColor: alpha(theme.palette.success.main, 0.24),
                                bgcolor: alpha(theme.palette.success.main, 0.06),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.success.main, 0.14),
                                    transform: 'translateY(-1px)'
                                }
                            }}
                        >
                            {isDownloading ? (
                                <CircularProgress size={17} color='inherit' />
                            ) : (
                                <Icon icon='mdi:file-pdf-box' fontSize={20} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title='Hapus dokumen' placement='top' arrow>
                    <IconButton
                        size='small'
                        color='error'
                        onClick={() => setDeleteTarget(row)}
                        sx={{
                            width: 34,
                            height: 34,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.error.main, 0.24),
                            bgcolor: alpha(theme.palette.error.main, 0.06),
                            '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.14),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        <Icon icon='mdi:trash-can-outline' fontSize={19} />
                    </IconButton>
                </Tooltip>
            </Stack>
        )
    }

    const ActionButtonsAdd = ({ row }: { row: InvoiceReceiptRow }) => (
        <Tooltip title='Buat Invoice Receipt' placement='top' arrow>
            <IconButton
                size='small'
                color='primary'
                onClick={() => handleAddFromSupplier(row)}
                sx={{
                    width: 36,
                    height: 36,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.24),
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.14),
                        transform: 'translateY(-1px)'
                    }
                }}
            >
                <Icon icon='mdi:plus-circle-outline' fontSize={21} />
            </IconButton>
        </Tooltip>
    )

    const actionData = [
        {
            key: 'aksi',
            field: 'aksi',
            headerName: 'Aksi',
            sortable: false,
            filterable: false,
            minWidth: 165,
            flex: 0.65,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }: { row: InvoiceReceiptRow }) => <ActionButtons row={row} />
        }
    ]

    const actionDataAdd = [
        {
            key: 'aksi',
            field: 'aksi',
            headerName: 'Aksi',
            sortable: false,
            filterable: false,
            minWidth: 90,
            flex: 0.35,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row }: { row: InvoiceReceiptRow }) => <ActionButtonsAdd row={row} />
        }
    ]

    const renderTableContent = (actions: any[]) => {
        if (loading) {
            return (
                <Box
                    sx={{
                        minHeight: 360,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1.5
                    }}
                >
                    <CircularProgress size={36} thickness={4.2} />
                    <Typography variant='body2' color='text.secondary'>
                        Memuat data Invoice Receipt...
                    </Typography>
                </Box>
            )
        }

        if (listData.length === 0) {
            return (
                <Box
                    sx={{
                        minHeight: 360,
                        px: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        textAlign: 'center'
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            width: 72,
                            height: 72,
                            mb: 2,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }}
                    >
                        <Icon
                            icon={tabs === '0' ? 'mdi:file-document-outline' : 'mdi:truck-delivery-outline'}
                            fontSize={34}
                        />
                    </Paper>

                    <Typography variant='h6' sx={{ fontWeight: 700, mb: 0.75 }}>
                        Data belum tersedia
                    </Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 430, mb: 2.25 }}>
                        Tidak ada data pada periode yang dipilih. Ubah rentang tanggal atau muat ulang data.
                    </Typography>
                    <Button
                        variant='outlined'
                        startIcon={<Icon icon='mdi:refresh' />}
                        onClick={() => void getData(tabs)}
                    >
                        Muat Ulang
                    </Button>
                </Box>
            )
        }

        return <MsiDatagrid array={listData} actions={actions} />
    }

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2, md: 3 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 4,
                            p: { xs: 2.5, md: 3.5 },
                            color: 'common.white',
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.info.main} 100%)`,
                            boxShadow: `0 18px 45px ${alpha(theme.palette.primary.dark, 0.24)}`,
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                width: 250,
                                height: 250,
                                right: -90,
                                top: -115,
                                borderRadius: '50%',
                                bgcolor: alpha(theme.palette.common.white, 0.1)
                            }
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2.5}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent='space-between'
                            sx={{ position: 'relative', zIndex: 1 }}
                        >
                            <Stack direction='row' spacing={2} alignItems='center'>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        width: 58,
                                        height: 58,
                                        flexShrink: 0,
                                        borderRadius: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'common.white',
                                        bgcolor: alpha(theme.palette.common.white, 0.16),
                                        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                                        backdropFilter: 'blur(8px)'
                                    }}
                                >
                                    <Icon icon='mdi:receipt-text-check-outline' fontSize={31} />
                                </Paper>

                                <Box>
                                    <Typography
                                        variant='overline'
                                        sx={{ opacity: 0.78, letterSpacing: 1.4, fontWeight: 700 }}
                                    >
                                        Accounting & Payable
                                    </Typography>
                                    <Typography
                                        variant='h4'
                                        sx={{
                                            fontWeight: 800,
                                            lineHeight: 1.15,
                                            fontSize: { xs: '1.65rem', md: '2rem' }
                                        }}
                                    >
                                        Invoice Receipt
                                    </Typography>
                                    <Typography variant='body2' sx={{ mt: 0.75, opacity: 0.84, maxWidth: 620 }}>
                                        Kelola dokumen penerimaan invoice dan pantau GR yang belum tertagih dalam satu halaman.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Button
                                variant='contained'
                                size='large'
                                startIcon={<Icon icon='mdi:plus' />}
                                onClick={() => navigate('/acc/invoice-receipt/add')}
                                sx={{
                                    flexShrink: 0,
                                    minHeight: 46,
                                    px: 2.5,
                                    borderRadius: 2.5,
                                    color: 'primary.main',
                                    bgcolor: 'common.white',
                                    boxShadow: 'none',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.common.white, 0.92),
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Buat Invoice Receipt
                            </Button>
                        </Stack>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.045)}`
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent='space-between'
                                sx={{ mb: 2.5 }}
                            >
                                <Box>
                                    <Typography variant='h6' sx={{ fontWeight: 750 }}>
                                        Filter Periode
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Pilih rentang tanggal untuk menampilkan dokumen yang dibutuhkan.
                                    </Typography>
                                </Box>

                                <Chip
                                    size='small'
                                    icon={<Icon icon='mdi:database-outline' fontSize={17} />}
                                    label={`${listData.length} data`}
                                    sx={{ fontWeight: 650 }}
                                />
                            </Stack>

                            <Grid container spacing={2} alignItems='center'>
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        label='Tanggal Awal'
                                        type='date'
                                        value={tglAwal}
                                        onChange={event => setTglAwal(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        fullWidth
                                        size='small'
                                        label='Tanggal Akhir'
                                        type='date'
                                        value={tglAkhir}
                                        onChange={event => setTglAkhir(event.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md='auto'>
                                    <Button
                                        fullWidth
                                        variant='contained'
                                        disabled={loading}
                                        startIcon={
                                            loading ? <CircularProgress size={17} color='inherit' /> : <Icon icon='mdi:magnify' />
                                        }
                                        onClick={handleSearch}
                                        sx={{ minHeight: 40, px: 2.5, borderRadius: 2 }}
                                    >
                                        Cari Data
                                    </Button>
                                </Grid>

                                <Grid item xs={12} sm={6} md='auto'>
                                    <Button
                                        fullWidth
                                        variant='outlined'
                                        disabled={loading}
                                        startIcon={<Icon icon='mdi:filter-remove-outline' />}
                                        onClick={handleResetFilter}
                                        sx={{ minHeight: 40, px: 2.25, borderRadius: 2 }}
                                    >
                                        Reset
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Card
                        elevation={0}
                        sx={{
                            overflow: 'hidden',
                            borderRadius: 3.5,
                            border: '1px solid',
                            borderColor: 'divider',
                            boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.045)}`
                        }}
                    >
                        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 2.5 } }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                justifyContent='space-between'
                            >
                                <Box>
                                    <Typography variant='h6' sx={{ fontWeight: 750 }}>
                                        Daftar Invoice Receipt
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Gunakan menu aksi untuk melihat detail, mengunduh PDF, atau menghapus dokumen.
                                    </Typography>
                                </Box>

                                <Chip
                                    size='small'
                                    color={tabs === '0' ? 'primary' : 'warning'}
                                    variant='outlined'
                                    label={tabs === '0' ? 'Semua Data' : 'GR Belum Tertagih'}
                                    sx={{ fontWeight: 650 }}
                                />
                            </Stack>
                        </Box>

                        <TabContext value={tabs}>
                            <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.5 }}>
                                <TabList
                                    variant='scrollable'
                                    scrollButtons='auto'
                                    allowScrollButtonsMobile
                                    onChange={handleChangeTabs}
                                    aria-label='Tab Invoice Receipt'
                                    sx={{
                                        minHeight: 48,
                                        '& .MuiTabs-indicator': {
                                            height: 3,
                                            borderRadius: '3px 3px 0 0'
                                        },
                                        '& .MuiTab-root': {
                                            minHeight: 48,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderRadius: '10px 10px 0 0'
                                        }
                                    }}
                                >
                                    <Tab
                                        value='0'
                                        icon={<Icon icon='mdi:format-list-bulleted' fontSize={19} />}
                                        iconPosition='start'
                                        label='Semua Data'
                                    />
                                    <Tab
                                        value='1'
                                        icon={<Icon icon='mdi:truck-alert-outline' fontSize={19} />}
                                        iconPosition='start'
                                        label='GR Belum Tertagih'
                                    />
                                </TabList>
                            </Box>

                            <Divider />

                            <TabPanel value='0' sx={{ p: { xs: 1, md: 2 } }}>
                                {renderTableContent(actionData)}
                            </TabPanel>

                            <TabPanel value='1' sx={{ p: { xs: 1, md: 2 } }}>
                                {renderTableContent(actionDataAdd)}
                            </TabPanel>
                        </TabContext>
                    </Card>
                </Grid>
            </Grid>

            <Dialog
                open={Boolean(deleteTarget)}
                onClose={() => {
                    if (!deleting) setDeleteTarget(null)
                }}
                maxWidth='xs'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3.5,
                        p: 0.5
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Stack direction='row' spacing={1.5} alignItems='center'>
                        <Paper
                            elevation={0}
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'error.main',
                                bgcolor: alpha(theme.palette.error.main, 0.09)
                            }}
                        >
                            <Icon icon='mdi:trash-can-outline' fontSize={23} />
                        </Paper>
                        <Typography variant='h6' component='span' sx={{ fontWeight: 750 }}>
                            Hapus Dokumen?
                        </Typography>
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        Dokumen <strong>{getRowDocumentLabel(deleteTarget)}</strong> akan dihapus. Tindakan ini tidak dapat dibatalkan.
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant='outlined'
                        color='inherit'
                        disabled={deleting}
                        onClick={() => setDeleteTarget(null)}
                    >
                        Batal
                    </Button>
                    <Button
                        variant='contained'
                        color='error'
                        disabled={deleting}
                        startIcon={
                            deleting ? <CircularProgress size={17} color='inherit' /> : <Icon icon='mdi:trash-can-outline' />
                        }
                        onClick={() => void handleDelete()}
                    >
                        Hapus Dokumen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
